import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import imagesLoaded from 'imagesloaded';

// Locale Imports
import en_common from './locales/en/common.json';
import en_footer from './locales/en/footer.json';
import en_helpText from './locales/en/helpText.json';
import en_searchForm from './locales/en/searchForm.json';
import en_upperNav from './locales/en/upperNav.json';
import en_mainRight from './locales/en/mainRight.json';
import en_sustainability from './locales/en/sustainability.json';
import en_treatment from './locales/en/treatment.json';
import en_cm from './locales/en/cm.json';
import en_fp from './locales/en/fp.json';
import en_fpgb from './locales/en/fpgb.json';
import en_certs from './locales/en/certs.json';
import en_species from './locales/en/species.json';
import en_origin from './locales/en/origin.json';
import en_cleanliness from './locales/en/cleanliness.json';
import en_content from './locales/en/content.json';
import en_processing from './locales/en/processing.json';
import en_care from './locales/en/care.json';

import fr_common from './locales/fr/common.json';
import fr_footer from './locales/fr/footer.json';
import fr_helpText from './locales/fr/helpText.json';
import fr_searchForm from './locales/fr/searchForm.json';
import fr_upperNav from './locales/fr/upperNav.json';
import fr_mainRight from './locales/fr/mainRight.json';
import fr_sustainability from './locales/fr/sustainability.json';
import fr_treatment from './locales/fr/treatment.json';
import fr_cm from './locales/fr/cm.json';
import fr_fp from './locales/fr/fp.json';
import fr_fpgb from './locales/fr/fpgb.json';
import fr_certs from './locales/fr/certs.json';
import fr_species from './locales/fr/species.json';
import fr_origin from './locales/fr/origin.json';
import fr_cleanliness from './locales/fr/cleanliness.json';
import fr_content from './locales/fr/content.json';
import fr_processing from './locales/fr/processing.json';
import fr_care from './locales/fr/care.json';

import "./App.css";
import * as tmdLogic from "./Dc.js";
import homePageHero from "./img/homepage-hero.mp4";
import UpperNavList from "./Components/UpperNavList.js";
import IntroBlock from "./Components/content/IntroBlock.js";
import { DetailPanel } from "./Components/DetailPanel.js";
import { ContentSelector } from "./Components/ContentSelector.js";
import { ContentDetails } from "./Components/ContentDetails.js";
import CarbonCalculator from "./Components/CarbonCalculator.js";
import { lotException } from "./LotException.js";
import SearchForm from "./Components/SearchForm";
import * as searchOrchestrator from "./services/SearchOrchestrator";

// GTM Initialization
import TagManager from "react-gtm-module";
const tagManagerArgs = {
  gtmId: "GTM-557QGNJ"
};

// Utility Imports
import { applyUIState, triggerSectionTransition, trackAnalyticsEvent } from "./utils/ui.utils";
import { getApiUrl, SUSTAINABILITY_DEV_MODE as DEV_MODE, forceShowImpact, forceHideCalculator } from './utils/url.utils';
import { calculateImpact } from "./utils/impact.utils.js";

// Translation Flattening Logic
const addPrefix = (obj, prefix) => {
  const flatten = (data, pref) => {
    return Object.keys(data).reduce((acc, key) => {
      const fullKey = pref ? `${pref}.${key}` : key;
      if (typeof data[key] === 'object' && data[key] !== null && !Array.isArray(data[key])) {
        Object.assign(acc, flatten(data[key], fullKey));
      } else {
        acc[fullKey] = data[key];
      }
      return acc;
    }, {});
  };
  return flatten(obj, prefix);
};

const en = { ...addPrefix(en_common, ''), ...addPrefix(en_footer, 'footer'), ...addPrefix(en_helpText, 'helpText'), ...addPrefix(en_searchForm, 'searchForm'), ...addPrefix(en_upperNav, 'upperNav'), ...addPrefix(en_mainRight, 'mainRight'), ...addPrefix(en_sustainability, 'sustainability'), ...addPrefix(en_treatment, 'treatment'), ...addPrefix(en_cm, 'cm'), ...addPrefix(en_fp, 'fp'), ...addPrefix(en_fpgb, 'fpgb'), ...addPrefix(en_certs, 'certs'), ...addPrefix(en_species, 'species'), ...addPrefix(en_origin, 'origin'), ...addPrefix(en_cleanliness, 'cleanliness'), ...addPrefix(en_content, 'content'), ...addPrefix(en_processing, 'processing'), ...addPrefix(en_care, 'care') };
const fr = { ...addPrefix(fr_common, ''), ...addPrefix(fr_footer, 'footer'), ...addPrefix(fr_helpText, 'helpText'), ...addPrefix(fr_searchForm, 'searchForm'), ...addPrefix(fr_upperNav, 'upperNav'), ...addPrefix(fr_mainRight, 'mainRight'), ...addPrefix(fr_sustainability, 'sustainability'), ...addPrefix(fr_treatment, 'treatment'), ...addPrefix(fr_cm, 'cm'), ...addPrefix(fr_fp, 'fp'), ...addPrefix(fr_fpgb, 'fpgb'), ...addPrefix(fr_certs, 'certs'), ...addPrefix(fr_species, 'species'), ...addPrefix(fr_origin, 'origin'), ...addPrefix(fr_cleanliness, 'cleanliness'), ...addPrefix(fr_content, 'content'), ...addPrefix(fr_processing, 'processing'), ...addPrefix(fr_care, 'care') };

export default function App(props) {
  const [active, setActive] = useState("LOT");
  const [db, setDb] = useState({});
  const [loading, setLoading] = useState(false);
  const [lotError, setLotError] = useState("");
  const [progressBarStatus, setProgressBarStatus] = useState('idle'); // 'idle', 'active', 'complete'  
  const [searchText, setSearchText] = useState("");
  const [searchValid, setSearchValid] = useState(-1);
  const [flag, setFlag] = useState(false);
  const [lang, setLang] = useState("en");
  const [draft, setDraft] = useState("");
  const [externalRules, setExternalRules] = useState(null);
  const [rootDatasetState, setRootDatasetState] = useState({}); // New state for #root dataset

  const [sectionTransitionTrigger, setSectionTransitionTrigger] = useState(null); // New state for triggering section transitions
  const [initialLoadCheckComplete, setInitialLoadCheckComplete] = useState(false); // New state to control initial rendering


  const dialogRef = useRef(null);
  const hasMounted = useRef(false);
  const searchInProgress = useRef(false); // STACK GUARD: Prevents infinite search calls
  const hasCheckedUrlLot = useRef(false); // NEW REF: To prevent double calling lookUpLot from initial useEffect

  // Function to fetch external rules (exceptions.json)
  const fetchExternalRules = useCallback(async () => {
    //console.log("APP: Fetching external rules...");
    const url = DEV_MODE
      ? "https://trackmydown.com/core/config/exceptions.json"
      : "https://trackmydown.com/core/config/exceptions.json"; // Corrected production path for exceptions.json
    try {
      const response = await fetch(url);
      if (!response.ok) { // Check if response was successful
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const rules = await response.json();
      if (hasMounted.current) { // Ensure component is still mounted
        setExternalRules(rules);
        //console.log("APP: External rules fetched and set."); // Added log for success
      }
    } catch (e) {
      //console.warn("APP: External lot rules could not be loaded:", e); // Keep existing warning
      setExternalRules([]); // Set to empty array to prevent null/undefined issues
    }
  }, [hasMounted]); // hasMounted in dependency array to avoid stale closures, though it's a ref.

  // Effect to set hasMounted ref
  useEffect(() => {
    hasMounted.current = true;
    return () => {
      hasMounted.current = false;
    };
  }, []);

  // Effect to fetch external rules once on mount
  useEffect(() => {
    fetchExternalRules();
  }, [fetchExternalRules]);

  // Effect to initialize Google Tag Manager (GTM)
  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_GA !== "true") {
      TagManager.initialize(tagManagerArgs);
    }
  }, []); // Empty dependency array means it runs once on mount

  // Effect to immediately show shield if lot parameter is present on initial load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mountedLot = params.get("lot") || params.get("lotno");
    if (mountedLot) {
      document.body.classList.add("show-shield");
      setProgressBarStatus('active'); // Also trigger the progress bar immediately if a lot is found
    }
    setInitialLoadCheckComplete(true); // Mark initial check as complete
  }, []); // Empty dependency array means it runs once on mount

  // 🚀 THE WATCHER: Applies classes to the existing <body>. 
  useEffect(() => {
    // Manage classes: Add/remove based on state
    if (loading) document.body.classList.add("is-loading");
    else document.body.classList.remove("is-loading");

    if (searchText !== "") document.body.classList.add("loaded");
    else document.body.classList.remove("loaded");

    if (!loading && searchText === "") document.body.classList.add("intro");
    else document.body.classList.remove("intro");

    // Manage data-active attribute
    if (searchText !== "") {
      document.body.dataset.active = active;
    } else {
      delete document.body.dataset.active; // Remove if not in Beta UI for Lot
    }

  }, [loading, searchText, active]);

  // 🚀 THE WATCHER for #root dataset
  useEffect(() => {
    const rootElement = document.querySelector("#root");
    if (rootElement) {
      for (const key in rootDatasetState) {
        if (Object.hasOwnProperty.call(rootDatasetState, key)) {
          rootElement.dataset[key] = rootDatasetState[key];
        }
      }
    }
  }, [rootDatasetState]);

  // 🚀 THE WATCHER: Processes sectionTransitionTrigger for animated class changes
  useEffect(() => {
    if (!sectionTransitionTrigger) return;

    // Synchronize the body dataset immediately so CSS can react
    document.body.dataset.active = sectionTransitionTrigger;

    const actions = triggerSectionTransition(sectionTransitionTrigger);
    const timeouts = [];

    actions.forEach(action => {
      const timeoutId = setTimeout(() => {
        if (action.type === 'add') {
          document.body.classList.add(action.className);
        } else if (action.type === 'remove') {
          document.body.classList.remove(action.className);
        }
      }, action.delay);
      timeouts.push(timeoutId);
    });

    // Cleanup function to clear timeouts if component unmounts or trigger changes
    return () => {
      timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    };

  }, [sectionTransitionTrigger]); // Re-run when sectionTransitionTrigger changes

  // TEMP: imagesLoaded test
  useEffect(() => {
    if (!searchText) return;
  
    document.body.classList.add('loaded');
    document.body.classList.add('wipeOne');
    document.body.classList.add('imgUp');
    document.body.classList.add('wipeLeft');
    document.body.classList.add('add');
  
    const el = document.querySelector('.upper-bg');
    
    imagesLoaded('.il', { background: true }, function () {    
      setTimeout(() => {          
        setTimeout(() => {    
          document.body.classList.remove('wipeOne');
          document.body.classList.remove('wipeLeft');
        }, 1260);    
      }, 1000);
    });

  
  }, [searchText]);
  
  const t = useCallback((key, variables = {}) => {
    const translations = { en, fr };
    const currentTranslations = translations[lang] || en;
    if (!(key in currentTranslations)) return variables.returnObjects ? null : key;
    let translation = currentTranslations[key];
    if (typeof translation === 'string') {
      Object.keys(variables).forEach(varName => {
        const regex = new RegExp(`{{${varName}}}`, 'g');
        translation = translation.replace(regex, variables[varName]);
      });
    }
    return translation;
  }, [lang]);

  const showImpact = forceShowImpact() !== null ? forceShowImpact() : (DEV_MODE || db.showImpact === true);
  const hideCalculator = forceHideCalculator() !== null ? forceHideCalculator() : !!db.hideCalculator;

  const impactData = useMemo(() => {
    if (!db || Object.keys(db).length === 0) return null;
    return calculateImpact(db);
  }, [db]);
    
  const handleExternalLinkClick = useCallback((e) => {
    const linkElement = e.currentTarget;
    const url = linkElement.href;
    const text = linkElement.textContent.trim();
    
    // Check if the link is external (starts with http/https and is not on the current domain)
    const isExternal = linkElement.target === '_blank' || 
                       (url.startsWith('http') && !url.startsWith(window.location.origin));

    if (isExternal) {
      trackAnalyticsEvent({
        event: 'external_link_click',
        event_category: 'Outbound Navigation',
        event_action: 'Click',
        event_label: url,
        link_text: text // Custom dimension for link text
      });
    }
  }, []);

  const handleLanguageChange = useCallback((newLang) => {
    setLang(newLang);
    trackAnalyticsEvent({
      event: 'language_change',
      event_category: 'User Preferences',
      event_action: 'Select',
      event_label: newLang
    });
  }, []);

  const handleLotError = useCallback((param2) => {
    //console.log("App.js: handleLotError - START for lot:", param2);
    setLoading(false);
    setProgressBarStatus('complete');
    // Start content reveal delay
    setTimeout(() => {
      document.body.classList.remove("show-shield"); // Deactivate the shield
      setProgressBarStatus('idle'); // Progress bar becomes idle and fades out
    }, 1000); // Delay before content reveal and shield/bar fade-out
    setLotError(param2);
    setFlag(true);
    setSearchValid(1);
    searchInProgress.current = false; // Release guard

    const newUrl = `${window.location.pathname}?lot=${param2}&nf=1`; // Removed &engine=beta

    window.history.replaceState({ path: newUrl }, '', newUrl);

    // Track "Lot Not Found" error
    trackAnalyticsEvent({
      event: 'lot_search',
      event_category: 'Lot Search',
      event_action: 'Error',
      event_label: param2,
      error_type: 'Not Found'
    });
  }, []);
  
  const processNormalizedLot = useCallback(async (docData, lotNumber) => {
    // START Global Progress Bar and UI Shield for valid data processing
    setProgressBarStatus('active');
    document.body.classList.add("show-shield");
    //console.log("App.js: processNormalizedLot - START for lot:", lotNumber);
    // Capture the return value from applyUIState
    const uiStateResult = applyUIState(docData, lotNumber);
    
    // Apply rootDataAttributes to state
    setRootDatasetState(uiStateResult.rootDataAttributes);

    // If applyUIState returned docData, use that for subsequent processing
    // Otherwise, continue using the original docData
    const dataForProcessing = uiStateResult.docData || docData;

    const logicStatus = tmdLogic.getUsefulContents(dataForProcessing);
  
    let brandResult;
    try {
      brandResult = await tmdLogic.brandList(docData.prefix);
      if (!brandResult) {
        brandResult = {
          prefix: docData.prefix || "0000",
          logo: true,
          brandName: "ALLIED FEATHER + DOWN",
          url: null
        };
      }
    } catch (error) {
      brandResult = { prefix: "0000", logo: true, brandName: "ALLIED FEATHER + DOWN" };
    }
  
    //console.log("APP: Calling lotException with lotNumber:", lotNumber, "and externalRules:", externalRules);
    const exceptionResult = await lotException(lotNumber, externalRules);
    let brandNameAltValue = "";
    let display_lotAltValue = null;
    let oFlagValue = false;
    let brandPrefixAltValue = null;
    let brandLogoAltValue = undefined;
    let aafaValue = false;
  
    if (exceptionResult && !Array.isArray(exceptionResult)) {
      brandNameAltValue = exceptionResult.brandName;
      display_lotAltValue = exceptionResult.display_lot;
    } else if (Array.isArray(exceptionResult)) {
      switch (exceptionResult[0]) {
        case "brandName":
          brandNameAltValue = exceptionResult[1];
          if (exceptionResult[2] !== undefined) {
            brandPrefixAltValue = exceptionResult[2];
            brandLogoAltValue = exceptionResult[3];
          }
          break;
        case "display_lot":
          display_lotAltValue = exceptionResult[1];
          break;
        case "origin":
          oFlagValue = true;
          break;
        case "aafa":
          aafaValue = true;
          break;
      }
    }
  
    setDb({
      ...docData,      
      normalizedTreatment: (() => {
        let t = ""; // Initialize t as an empty string

        if (Array.isArray(docData.treatment) && docData.treatment.length > 0) {
          t = docData.treatment[0];
        } else if (typeof docData.treatment === 'string') { // Handle cases where treatment might directly be a string
          t = docData.treatment;
        }

        const brandMap = {
          "hyperdry": "HyperDRY",
          "expedry": "ExpeDRY",
          "biodown": "BioDown"
        };

        const key = t.toLowerCase(); // Now t is guaranteed to be a string
        return brandMap[key] ? brandMap[key] : t;
      })(),
      normalizedFacility: docData.facility || "", // Add normalizedFacility
      prefix: docData.prefix,
      brand: brandResult.prefix,
      brandName: brandResult.brandName,
      brandLogo: brandResult.logo,
      brandLogoAlt: brandLogoAltValue,
      brandNameAlt: brandNameAltValue,
      brandPrefixAlt: brandPrefixAltValue,
      display_lotAlt: display_lotAltValue,
      oFlag: oFlagValue,
      brandLink: brandResult.url,
      rds: logicStatus.rdsStatus,
      rsd: logicStatus.rsdStatus || false,
      recycled: docData.recycled || false,
      chinaDomestic: docData.chinaDomestic || false,
      math: uiStateResult.rootDataAttributes.math,
      stated_fp_gb: docData.stated_fp_gb || null,
      verified_fp_gb: docData.verified_fp_gb || null,
      aafa: aafaValue, 
    });
  
    if (docData.source && ['CDN', 'Firestore', 'Firestore Proxy', 'Legacy V1 API'].includes(docData.source)) {
      // ADD THIS NEW TRACKING EVENT
      trackAnalyticsEvent({
        event: 'lot_rendered_beta_ui',

        event_category: 'UI Rendering',
        event_action: 'View', // Changed from 'Beta UI Display'
        event_label: lotNumber,
        source: docData.source
      });
    }
    setActive("LOT");
    setSectionTransitionTrigger("LOT"); // Trigger the useEffect

    setLoading(false); 
    setSearchText(lotNumber.toUpperCase()); 
    searchInProgress.current = false; // Release guard
    setProgressBarStatus('complete');
    //console.log("App.js: processNormalizedLot - setProgressBarStatus('complete')");
    // Start content reveal delay
    setTimeout(() => {
      document.body.classList.remove("show-shield"); // Deactivate the shield
      setProgressBarStatus('idle'); // Progress bar becomes idle and fades out
      //console.log("App.js: processNormalizedLot - setProgressBarStatus('idle')");
    }, 1000); // Delay before content reveal and shield/bar fade-out

    // Track successful lot processing
    trackAnalyticsEvent({
      event: 'lot_processed',
      event_category: 'Lot Search',
      event_action: 'Success',
      event_label: lotNumber,
      source: docData.source // e.g., 'CDN', 'Firestore', 'Legacy V1 API'
    });
  }, [externalRules, applyUIState, triggerSectionTransition]);

  const handleClose = useCallback(() => { 
    // Track Lot Detail Panel Close
    if (searchText) { // Only track if a lot was actually displayed
      trackAnalyticsEvent({
        event: 'lot_detail_close',
        event_category: 'Lot Navigation',
        event_action: 'Close',
        event_label: searchText // The lot number being closed
      });
    }

    setLoading(false); 
    setSearchText("");
    setDb({});
    setFlag(false);
    searchInProgress.current = false;
  }, [searchText]);

  
  const lookUpLot = useCallback(async (e, param2) => {
    // Clear any existing 'nf' (not found) parameter from the URL
    const url = new URL(window.location);
    url.searchParams.delete('nf');
    window.history.replaceState({}, '', url);

    if (!param2 || searchInProgress.current) {
      return;
    }
    const cleanLot = param2.trim().toUpperCase();

    // Regex for lot number format validation (e.g., alphanumeric, hyphens, no special characters other than hyphen)
    const lotFormatRegex = /^[A-Z0-9-]+$/; // Example: 12345-AB-678, L21F1861, etc. Adjust as needed.

    if (!lotFormatRegex.test(cleanLot)) {
      setLoading(false); // Hide the "VERIFYING..." bar
      // Note: handleLotError expects a param2. It sets searchValid(1) which displays the 'invalid entry' message.
      handleLotError(cleanLot); 
      return; // Stop processing for invalid format
    }
  
    // Guard: Don't search for the exact same thing if it's already active
    if (cleanLot === searchText) {
      return;
    }

    // NEW: Track search_start as soon as user intent is confirmed
    trackAnalyticsEvent({
      event: 'search_start',
      event_category: 'Search Form',
      event_action: 'Initiated',
      event_label: cleanLot
    });

    // Track Search Submission
    trackAnalyticsEvent({
      event: 'search_initiated',
      event_category: 'Search Form',
      event_action: 'Submit',
      event_label: cleanLot
    });
  
    searchInProgress.current = true; // Lock guard
    setLoading(true); 
    setFlag(false);
  
    searchOrchestrator.executeSearch(cleanLot, { syncToFirestore: true })
      .then(docData => {
        if (docData) {
          processNormalizedLot(docData, cleanLot);
        } else {
          handleLotError(cleanLot); 
        }
      })
      .catch((err) => {
        console.error("App.js: lookUpLot - executeSearch rejected with error:", err);
        if (err.isHalt) {
          console.warn("SEARCH HALTED BY QA BREAKPOINT. UI REMAINS IN LOADING STATE FOR INSPECTION.");
          searchInProgress.current = false; // Release guard
        }
        handleLotError(cleanLot);
      });
  }, [processNormalizedLot, handleLotError, searchText]);
  
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const lotInUrl = params.get("lot") || params.get("lotno");
      if (!lotInUrl) handleClose();
      else if (lotInUrl.toUpperCase() !== searchText) lookUpLot("", lotInUrl);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [handleClose, lookUpLot, searchText]);
  
  useEffect(() => {
    // This is the actual useEffect that triggers lookUpLot
    if (hasCheckedUrlLot.current) {
        //console.log("APP: useEffect returning early because URL lot already checked (hasCheckedUrlLot).");
        return;
    }
    if (!externalRules || searchText !== "") return;
    
    const params = new URLSearchParams(window.location.search);
    const mountedLot = params.get("lot") || params.get("lotno");
    
    if (mountedLot && mountedLot !== searchText) {
        hasCheckedUrlLot.current = true; // Mark as checked before calling to prevent re-entry
        lookUpLot("", mountedLot); 
    } else if (!mountedLot) {
        hasCheckedUrlLot.current = true; // Mark as checked if no lot is found
    }
  }, [externalRules, lookUpLot, searchText]);  

  // NEW LOGGING USEEFFECT FOR INITIAL LOT SNIFFING
  useEffect(() => {
    //console.log("APP: [INIT] Initial lot sniffing useEffect triggered.");
    //console.log("APP: [INIT] externalRules:", externalRules, "searchText:", searchText);

    if (!externalRules) {
      //console.log("APP: [INIT] useEffect returning early because externalRules is null.");
      return;
    }
    if (searchText !== "") {
      //console.log("APP: [INIT] useEffect returning early because searchText is not empty.");
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const mountedLot = params.get("lot") || params.get("lotno");
    //console.log("APP: [INIT] mountedLot from URL:", mountedLot);

    if (mountedLot && mountedLot !== searchText) {
        //console.log("APP: [INIT] Calling lookUpLot from initial useEffect with:", mountedLot);
        // The actual call to lookUpLot is handled by the existing useEffect above.
        // This useEffect is purely for logging the decision process.
    } else if (mountedLot && mountedLot === searchText) {
        //console.log("APP: [INIT] Mounted lot matches current searchText, not calling lookUpLot.");
    } else {
        //console.log("APP: [INIT] No mounted lot found in URL, or mountedLot is empty.");
    }
  }, [externalRules, searchText]); // Removed lookUpLot from dependencies here as this is purely for logging the conditions.

  return (
    <>
      <div 
        className={`loading-progress-bar ${progressBarStatus === 'active' ? 'is-active' : ''} ${progressBarStatus === 'complete' ? 'is-complete' : ''}`}
      ></div>
      {initialLoadCheckComplete ? (
        <>
          <header>
            <a href="/">
              <h3>Track My Down</h3>
            </a>
          </header>

          {searchText !== "" && (
            <nav className="interiorNav">
              <ul>
                <UpperNavList lang={lang} searchText={searchText} />
                <li>
                  <div className="language">
                    <select value={lang} onChange={(e) => handleLanguageChange(e.target.value)}>
                      <option value="en">EN</option>
                      <option value="fr">FR</option>
                    </select>
                  </div>
                </li>
              </ul>
            </nav>
          )}

          {(searchText !== "") ? (
            //console.log("App.js: Rendering Beta UI for Lot"),
            <>
              <section className="main-left">
                <div className="contentWrap">
                  <ContentSelector onChange={(newSection) => { 
                    setActive(newSection); 
                    setSectionTransitionTrigger(newSection); 
                    // Track section change event
                    trackAnalyticsEvent({
                      event: 'section_view',
                      event_category: 'Lot Navigation',
                      event_action: 'View Section',
                      event_label: newSection,
                      lot_number: searchText 
                    });
                  }} activeSection={active} data={db} t={t} />
                  <ContentDetails activeSection={active} data={db} t={t} impactData={impactData} showImpact={showImpact} />
                </div>
              </section>
              <DetailPanel activeSection={active} data={db} t={t} onChange={setActive} aafa={db.aafa} onClose={handleClose} openDialog={() => {
  dialogRef.current?.showModal();
  trackAnalyticsEvent({
    event: 'carbon_calculator_interaction',
    event_category: 'Carbon Calculator',
    event_action: 'Open',
    event_label: db.display_lot || db.display_lotAlt // The lot number for which the calculator is opened
  });
}} showImpact={showImpact} hideCalculator={hideCalculator} />
              <small className="upper-credit">ISPO AWARD - GOLD WINNER 2019</small>
              <footer>a division of allied feather + down</footer>
            </>
          ) : (
            //console.log("App.js: Rendering Homepage"),
            <section className="homePage">
                <div className={`formWrap ${loading ? 'is-loading' : ''}`}>
                  <SearchForm onHelp={() => setFlag(!flag)} onSearch={lookUpLot} searchValid={setSearchValid} searchDraft={setDraft} activeLang={lang} t={t} loading={loading} />
                  {loading && <div className="search-progress-bar"><p>VERIFYING...</p><div className="loading-line"></div></div>}
                  <h1>TRACK MY DOWN</h1>
                  {flag && (
                    <div className="homePage-help">
                      {lotError !== "" && (
                        <>
                          <p>
                            Lot N<sup>o</sup> <span>{lotError}</span>{" "}
                            {searchValid === 1 ? (
                              t("helpText.cannotBeFound")
                            ) : (
                              <span dangerouslySetInnerHTML={{ __html: t("helpText.invalidEntry") }} />
                            )}
                          </p>
                          <br />
                          <p>
                            {t("helpText.believeError")}{" "}                          
                            <a href={`mailto:help@trackmydown.com?subject=TMD Help | lot ${lotError} not found`}>
                              {t("helpText.messageUs")}<br /><br />
                            </a>
                          </p>
                        </>
                      )}
                      <p>{t("helpText.troubleLocating")} {t("helpText.canHelp")}</p>
                      <p>
                        <a href="mailto:help@trackmydown.com?subject=TMD Help" tabIndex="0">
                          {t("helpText.messageUs")}
                        </a>
                      </p>
                    </div>
                )}
                </div>

                <nav>
                  <a href="//alliedfeather.com" target="_blank" onClick={handleExternalLinkClick}>                
                    <h2>{t("upperNav.poweredBy")}</h2> 
                  </a>
                  <ul>
                    <li><a href="https://alliedfeather.com/shop" target="_blank" rel="noopener noreferrer" tabIndex="0">{t("upperNav.adw")}</a></li>
                    <li><a href="https://alliedfeather.com/info-room/down-101/the-cluster" target="_blank" rel="noopener noreferrer" tabIndex="0">{t("upperNav.down101")}</a></li>
                    <li><a href="https://alliedfeather.com/downcare" target="_blank" rel="noopener noreferrer" tabIndex="0" onClick={handleExternalLinkClick}>{t("upperNav.downCare")}</a></li>
                    <li><a href="https://alliedfeather.com/sourcing" target="_blank" rel="noopener noreferrer" tabIndex="0">{t("upperNav.responsibleSourcing")}</a></li>
                    <li><a href="https://alliedfeather.com/partners" target="_blank" rel="noopener noreferrer" tabIndex="0">{t("upperNav.partners")}</a></li>
                    <li><a className="interior-contact" href="mailto:help@trackmydown.com?subject=TMD Inquiry" tabIndex="0">{t("upperNav.contact")}</a></li>
                    <li>
                      <div className="language" role="radiogroup" aria-labelledby="language-switcher1">                    
                        <select value={lang} onChange={(e) => handleLanguageChange(e.target.value)}>
                          <option value="en">EN</option>
                          <option value="fr">FR</option>
                        </select>
                      </div>
                    </li>
                  </ul>
                </nav>

                <div className="homePage-bg">
                  <video className="wallpaperVideo" src={homePageHero} autoPlay muted loop />
                </div>
                <IntroBlock t={t} />

                <footer role="contentinfo" className="site-foot" tabIndex="1">
                  <nav>
                    <section className="wider">
                      <h3>A DIVISION OF ALLIED FEATHER + DOWN</h3>
                      <ul>
                        <li tabIndex="99"><a href="https://alliedfeather.com" target="_blank" rel="noopener noreferrer">AlliedFeather.com</a></li>
                        <li tabIndex="99"><a href="https://alliedhomebedding.com" target="_blank" rel="noopener noreferrer">AlliedHomeBedding.com</a></li>
                        <li tabIndex="99"><a href="https://www.linkedin.com/company/allied-feather-down" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
                        <li tabIndex="99"><a href="https://www.instagram.com/alliedfeatheranddown/" target="_blank" rel="noopener noreferrer">@alliedfeatheranddown</a></li>
                      </ul>
                    </section>
                    <section>
                      <h3>culture</h3>
                      <ul>
                        <li tabIndex="99"><a href="https://intothecold.online" target="_blank" rel="noopener noreferrer">IntoTheCold.Online</a></li>
                        <li tabIndex="99"><a href="https://www.instagram.com/zeronrth/" target="_blank" rel="noopener noreferrer">@zeronrth</a></li>
                      </ul>
                    </section>
                    <section>
                      <h3>contact us</h3>
                      <ul>
                        <li tabIndex="99"><a href="mailto:help@trackmydown.com?subject=TMD Inquiry" onClick={handleExternalLinkClick}>help@trackmydown.com</a></li>
                        <li tabIndex="99">6905 W Acco St, Montebello, CA<br /> Los Angeles, CA 90640</li>
                      </ul>
                    </section>
                  </nav>
                </footer>
              </section>
          )}
        </>
      ) : null}

      {(db.display_lot || db.display_lotAlt) && (
        <CarbonCalculator ref={dialogRef} onClose={() => {
          dialogRef.current?.close();
          trackAnalyticsEvent({
            event: 'carbon_calculator_interaction',
            event_category: 'Carbon Calculator',
            event_action: 'Close',
            event_label: db.display_lot || db.display_lotAlt // The lot number displayed in the calculator
          });
        }} activeLot={db.display_lotAlt || db.display_lot} impactScore={impactData?.total || 0} t={t} />
      )}    </>
  );
}